/**
 * ChecklistView Component
 *
 * Lists all checklists for a project, grouped by category.
 * Shows progress and completion status for each checklist.
 *
 * @module pages/technician/checklists/ChecklistView
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  FaArrowLeft,
  FaClipboardCheck,
  FaShieldAlt,
  FaTools,
  FaTruck,
  FaList,
  FaCog,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSpinner,
  FaFilter,
} from 'react-icons/fa';
import { getChecklists } from '../../../services/checklistService';
import { useAuth } from '../../../contexts/AuthContext';
import './ChecklistView.css';

/**
 * Roles the DATABASE actually lets read `project_checklists` /
 * `project_checklist_items` today.
 *
 * Every policy on both tables is literally
 * `profiles.role = ANY (ARRAY['employee','admin'])` — not `is_employee()`,
 * which would have covered `technician`. Verified against `pg_policies`: no
 * policy mentions technician, supervisor or is_technician. `employee` is not a
 * role this system issues any more (the four live roles are admin / supervisor
 * / technician / client), so in practice only `admin` gets rows.
 *
 * A technician's SELECT therefore returns zero rows WITHOUT an error, which is
 * indistinguishable client-side from "this project has no checklists". Telling
 * them "no hay checklists" is a lie the screen cannot detect on its own — so it
 * is stated here, from what the DB grants, instead of inferred from an empty
 * array. Delete this list (and the branch below) once the RLS migration
 * described in the P1-D6 report lands.
 */
const ROLES_THE_DB_LETS_READ_CHECKLISTS = ['admin', 'employee'];

// Category icons mapping
const CATEGORY_ICONS = {
  pre_work: FaClipboardCheck,
  safety: FaShieldAlt,
  quality: FaTools,
  completion: FaCheckCircle,
  delivery: FaTruck,
  custom: FaList,
  general: FaCog,
};

// Category labels
const CATEGORY_LABELS = {
  pre_work: 'Pre-trabajo',
  safety: 'Seguridad',
  quality: 'Calidad',
  completion: 'Cierre',
  delivery: 'Entrega',
  custom: 'Personalizado',
  general: 'General',
};

// Category descriptions
const CATEGORY_DESCRIPTIONS = {
  pre_work: 'Verificaciones antes de iniciar el trabajo',
  safety: 'EPP, permisos y seguridad',
  quality: 'Verificaciones tecnicas',
  completion: 'Checklist de cierre',
  delivery: 'Checklist de entrega final',
  custom: 'Checklists personalizados',
  general: 'Otros checklists',
};

const ChecklistView = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { profile } = useAuth();
  const canReadChecklists = ROLES_THE_DB_LETS_READ_CHECKLISTS.includes(profile?.role);

  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState(searchParams.get('filter') || 'all');

  // Load checklists
  const loadChecklists = useCallback(async () => {
    if (!projectId) return;

    try {
      setLoading(true);
      setError(null);

      const filters = {};
      if (activeFilter === 'pending') {
        filters.completed = false;
      } else if (activeFilter === 'completed') {
        filters.completed = true;
      }

      const { data, error: fetchError } = await getChecklists(projectId, filters);

      if (fetchError) throw fetchError;

      setChecklists(data || []);
    } catch (err) {
      console.error('[ChecklistView] Load error:', err);
      setError(err.message || 'Error al cargar checklists');
    } finally {
      setLoading(false);
    }
  }, [projectId, activeFilter]);

  useEffect(() => {
    loadChecklists();
  }, [loadChecklists]);

  // Handle filter change
  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    setSearchParams(filter === 'all' ? {} : { filter });
  };

  // Group checklists by type.
  //
  // The column is `checklist_type`; `project_checklists` has no `category`
  // column, so this used to read `undefined` for every row and dump the whole
  // list into "General / Otros checklists" no matter what it actually was.
  const groupedChecklists = checklists.reduce((groups, checklist) => {
    const category = checklist.checklist_type || 'general';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(checklist);
    return groups;
  }, {});

  // Order categories. Any `checklist_type` the DB holds that is not in this
  // list still gets rendered at the end — a hardcoded whitelist would silently
  // hide whole checklists (the header would count them, the body would not show
  // them).
  const KNOWN_CATEGORY_ORDER = [
    'pre_work',
    'safety',
    'quality',
    'completion',
    'delivery',
    'custom',
    'general',
  ];
  const orderedCategories = [
    ...KNOWN_CATEGORY_ORDER,
    ...Object.keys(groupedChecklists).filter((c) => !KNOWN_CATEGORY_ORDER.includes(c)),
  ];

  // Get progress color
  const getProgressColor = (progress) => {
    if (progress === 100) return 'var(--success-color, #28a745)';
    if (progress >= 50) return 'var(--warning-color, #ffc107)';
    return 'var(--primary-color, #0057B8)';
  };

  // Render checklist card
  const renderChecklistCard = (checklist) => {
    const isCompleted = checklist.is_completed || checklist.progress === 100;

    return (
      <div
        key={checklist.id}
        className={`checklist-card ${isCompleted ? 'completed' : ''}`}
        // The detail route is `checklists/:checklistId/detalle` (App.js). Without
        // the `/detalle` suffix this matched the LIST route
        // (`checklists/:projectId`) with the checklist id in the project slot, so
        // tapping a checklist just reloaded the same screen — now empty, because
        // no project has that id.
        onClick={() => navigate(`/tecnico/checklists/${checklist.id}/detalle`)}
      >
        <div className="checklist-card-content">
          <div className="checklist-card-header">
            <h3 className="checklist-card-title">{checklist.title}</h3>
            <div className="checklist-card-badges">
              {checklist.is_required !== false && (
                <span className="badge required">Requerido</span>
              )}
              {isCompleted && (
                <span className="badge completed">
                  <FaCheckCircle /> Completado
                </span>
              )}
            </div>
          </div>

          {/* Progress info */}
          <div className="checklist-card-progress">
            <div className="progress-info">
              <span className="progress-count">
                {checklist.completedItems} de {checklist.totalItems}
              </span>
              <span className="progress-percentage">{checklist.progress}%</span>
            </div>
            <div className="progress-bar">
              <div
                className="progress-bar-fill"
                style={{
                  width: `${checklist.progress}%`,
                  backgroundColor: getProgressColor(checklist.progress),
                }}
              />
            </div>
          </div>

          {/* Task reference if exists */}
          {checklist.task && (
            <div className="checklist-card-task">
              Tarea: {checklist.task.task_number} - {checklist.task.title}
            </div>
          )}
        </div>

        <div className="checklist-card-arrow">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
          </svg>
        </div>
      </div>
    );
  };

  // Render category section
  const renderCategorySection = (category) => {
    const categoryChecklists = groupedChecklists[category];
    if (!categoryChecklists || categoryChecklists.length === 0) return null;

    const CategoryIcon = CATEGORY_ICONS[category] || FaList;
    const completedCount = categoryChecklists.filter(c => c.is_completed || c.progress === 100).length;

    return (
      <div key={category} className="category-section">
        <div className="category-header">
          <div className="category-icon">
            <CategoryIcon />
          </div>
          <div className="category-info">
            <h2 className="category-title">{CATEGORY_LABELS[category] || category}</h2>
            <p className="category-description">{CATEGORY_DESCRIPTIONS[category] || ''}</p>
          </div>
          <div className="category-count">
            {completedCount}/{categoryChecklists.length}
          </div>
        </div>
        <div className="category-checklists">
          {categoryChecklists.map(renderChecklistCard)}
        </div>
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="checklist-view-loading">
        <FaSpinner className="spinner" />
        <p>Cargando checklists...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="checklist-view-error">
        <FaExclamationTriangle className="error-icon" />
        <p>{error}</p>
        <button className="retry-btn" onClick={loadChecklists}>
          Reintentar
        </button>
        <button className="back-btn" onClick={() => navigate(-1)}>
          Volver
        </button>
      </div>
    );
  }

  // Calculate totals
  const totalChecklists = checklists.length;
  const completedChecklists = checklists.filter(
    c => c.is_completed || c.progress === 100
  ).length;
  const overallProgress = totalChecklists > 0
    ? Math.round((completedChecklists / totalChecklists) * 100)
    : 0;

  return (
    <div className="checklist-view">
      {/* Header */}
      <div className="checklist-view-header">
        <button
          className="back-button"
          onClick={() => navigate(-1)}
          aria-label="Volver"
        >
          <FaArrowLeft />
        </button>
        <div className="header-info">
          <h1 className="page-title">Checklists</h1>
          <p className="page-subtitle">
            {completedChecklists} de {totalChecklists} completados
          </p>
        </div>
      </div>

      {/* Overall progress */}
      <div className="overall-progress-section">
        <div className="overall-progress-info">
          <span>Progreso general</span>
          <span className="overall-percentage">{overallProgress}%</span>
        </div>
        <div className="overall-progress-bar">
          <div
            className="overall-progress-fill"
            style={{
              width: `${overallProgress}%`,
              backgroundColor: getProgressColor(overallProgress),
            }}
          />
        </div>
      </div>

      {/* Filters */}
      <div className="checklist-filters">
        <button
          className={`filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
          onClick={() => handleFilterChange('all')}
        >
          <FaFilter /> Todos
        </button>
        <button
          className={`filter-btn ${activeFilter === 'pending' ? 'active' : ''}`}
          onClick={() => handleFilterChange('pending')}
        >
          Pendientes
        </button>
        <button
          className={`filter-btn ${activeFilter === 'completed' ? 'active' : ''}`}
          onClick={() => handleFilterChange('completed')}
        >
          <FaCheckCircle /> Completados
        </button>
      </div>

      {/* Checklists by category */}
      {totalChecklists === 0 && !canReadChecklists ? (
        <div className="empty-state">
          <FaExclamationTriangle className="empty-icon" />
          <h3>No tienes acceso a los checklists</h3>
          <p>
            Los permisos de checklists todavia no contemplan tu rol, asi que esta
            pantalla no puede mostrarte nada aunque el proyecto tenga checklists.
            Pedile a un administrador que los revise con vos.
          </p>
        </div>
      ) : totalChecklists === 0 ? (
        <div className="empty-state">
          <FaClipboardCheck className="empty-icon" />
          <h3>No hay checklists</h3>
          <p>
            {activeFilter === 'all'
              ? 'Este proyecto no tiene checklists asignados.'
              : activeFilter === 'pending'
              ? 'No hay checklists pendientes.'
              : 'No hay checklists completados.'}
          </p>
        </div>
      ) : (
        <div className="checklists-container">
          {orderedCategories.map(renderCategorySection)}
        </div>
      )}
    </div>
  );
};

export default ChecklistView;
